/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669593600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 28 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "cockroachdb",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Unique identifiers",
          "depth": 0,
          "id": "unique-identifiers"
        }, {
          "value": "Column types",
          "depth": 0,
          "id": "column-types"
        }, {
          "value": "Required permissions",
          "depth": 0,
          "id": "required-permissions"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      code: "code",
      ul: "ul",
      li: "li",
      h3: "h3"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "unique-identifiers",
        children: "Unique identifiers"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch requires a unique identifier in the destination Cockroach table to add, remove, and update rows. The column must be either a ", _jsx(_components.code, {
          children: "UNIQUE"
        }), " or ", _jsx(_components.code, {
          children: "PRIMARY KEY"
        }), " column. At least one Hightouch field must map to a unique column."]
      }), "\n", _jsx(_components.p, {
        children: "If you're unsure whether your mapping includes a compatible column, just try it. Hightouch will error if you need to change your mappings."
      }), "\n", _jsx(_components.h2, {
        id: "column-types",
        children: "Column types"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch works out of the box with all standard column types, including:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "BIGINT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "INT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "TEXT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "VARCHAR"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "TIMESTAMPTZ"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "BOOLEAN"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "DECIMAL"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If you see type errors, it may be because your SQL query is producing the wrong format. Message us on Intercom if you need help."
      }), "\n", _jsx(_components.h2, {
        id: "required-permissions",
        children: "Required permissions"
      }), "\n", _jsx(_components.p, {
        children: "Your Cockroach credentials must be able to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "SELECT"
          }), ", ", _jsx(_components.code, {
            children: "INSERT"
          }), ", ", _jsx(_components.code, {
            children: "UPDATE"
          }), " and ", _jsx(_components.code, {
            children: "DELETE"
          }), " rows from the destination table."]
        }), "\n", _jsxs(_components.li, {
          children: ["Perform the following queries:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "SHOW SCHEMAS"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "SHOW TABLES FROM <schema>"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "SHOW CONSTRAINTS FROM <schema>.<table>"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "SHOW COLUMNS FROM <schema>.<table>"
              })
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
